import { useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Eye } from '../../../../../components/icon/solar-icons.js'
import { PortalAvatar } from '../../../../../components/portal-avatar/portal-avatar.js'
import {
    fetchImpersonableUsers,
    type ImpersonableUser,
    startImpersonation,
} from '../../../utils/portal-api.js'
import './view-as-section.css'

export interface ViewAsSectionProps {
    lang: Language
    showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
}

/**
 * Settings → "View as": admin picks a portal user and the session starts
 * viewing the portal as them (warning banner + exit live in the app shell).
 */
export const ViewAsSection = ({ lang, showToast }: ViewAsSectionProps) => {
    const s = t.portal.viewAs
    const [users, setUsers] = useState<ImpersonableUser[]>([])
    const [search, setSearch] = useState('')
    const [startingId, setStartingId] = useState<number | null>(null)

    useEffect(() => {
        void (async () => {
            const r = await fetchImpersonableUsers()
            if (r.ok) setUsers(r.data)
        })()
    }, [])

    const handleStart = async (userId: number) => {
        setStartingId(userId)
        try {
            const result = await startImpersonation(userId)
            if (result.ok) {
                // Full reload so every cached state is rebuilt as the target;
                // land on the dashboard the target user would see.
                window.location.href = '/'
                return
            }
            showToast(s.startError(lang), 'error')
        } finally {
            setStartingId(null)
        }
    }

    const query = search.trim().toLowerCase()
    const visibleUsers = query
        ? users.filter(
              (u) =>
                  (u.name ?? '').toLowerCase().includes(query) ||
                  u.email.toLowerCase().includes(query),
          )
        : users

    return (
        <div className="portal-view-as">
            <p className="portal-view-as__description">{s.description(lang)}</p>

            <TextField
                className="portal-view-as__search"
                value={search}
                placeholder={s.searchPlaceholder(lang)}
                onChange={(value) => setSearch(value)}
            />

            {visibleUsers.length === 0 && (
                <p className="portal-view-as__empty">{s.emptyList(lang)}</p>
            )}

            <ul className="portal-view-as__list">
                {visibleUsers.map((user) => (
                    <li key={user.id} className="portal-view-as__row">
                        <PortalAvatar
                            name={user.name ?? user.email}
                            avatarUrl={user.avatar}
                            size="sm"
                        />
                        <div className="portal-view-as__identity">
                            <span className="portal-view-as__name">{user.name ?? user.email}</span>
                            <span className="portal-view-as__email">{user.email}</span>
                        </div>
                        <Button
                            size="small"
                            variant="secondary"
                            iconLeft={Eye}
                            text={s.viewAsButton(lang)}
                            isDisabled={startingId !== null}
                            onClick={() => void handleStart(user.id)}
                        />
                    </li>
                ))}
            </ul>
        </div>
    )
}
